import React, { useContext, useRef } from 'react';
import clsx from 'clsx';
import {
  UNSTABLE_Toast as AriaToast,
  UNSTABLE_ToastContent as ToastContent,
  Text as AriaText,
  useContextProps,
  ButtonContext,
  QueuedToast,
  ToastProps as AriaToastProps,
} from 'react-aria-components';
import styles from '@danske/sapphire-css/components/toast/toast.module.css';
import { CloseLarge } from '@danske/sapphire-icons/react';
import { ToastContext, ToastData } from './context';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { IconButton, IconButtonProps } from '../../IconButton';
import { ThemeRoot } from '../../ThemeRoot';
import { usePress } from '@react-aria/interactions';
import { useEvent } from '@react-aria/utils';
import { useThemeContext } from '../../SapphireProvider';

export type ToastProps<T extends ToastData> = AriaToastProps<T> &
  SapphireStyleProps & {
    toast: QueuedToast<T>;
    placement: 'top' | 'bottom';
  };

export const Toast = <T extends ToastData>({
  toast,
  zIndex = 100,
  placement,
  ...props
}: ToastProps<T>): React.JSX.Element => {
  const {
    styleProps: { style, className },
  } = useSapphireStyleProps(props);

  const ref = useRef<HTMLDivElement>(null);

  const { queue, overlayState } = useContext(ToastContext)!;

  const { pressProps } = usePress({
    onPress: () => {
      overlayState.toggle();
    },
    preventFocusOnPress: true,
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if ((e.target as Element)?.closest('button')) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      overlayState.toggle();
      if (overlayState.isOpen) {
        ref.current?.blur();
      }
    }
  };

  useEvent(ref, 'keydown', handleKeyDown);

  const index = queue?.visibleToasts.indexOf(toast) ?? 0;
  const isPreview = !overlayState.isOpen && index > 0;

  // Get theme to render Toast content with correct styles
  const { theme } = useThemeContext();
  const isDarkTheme = theme?.themeClassName.includes('dark');

  const wrappToastContentInTheme = (content: React.ReactNode) => {
    return toast.content.variant === 'neutral' || !isDarkTheme ? (
      <ThemeRoot variant="contrast" display="contents">
        {content}
      </ThemeRoot>
    ) : (
      content
    );
  };

  return (
    <AriaToast
      toast={toast}
      {...pressProps}
      className={({ isFocusVisible }) =>
        clsx(
          styles['sapphire-toast'],
          styles['js-focus'],
          {
            [styles['sapphire-toast--stacked']]: isPreview,
            [styles['is-focus']]: isFocusVisible,
          },
          className
        )
      }
      style={
        {
          ...style,
          viewTransitionName: toast.key,
          viewTransitionClass: clsx(
            /**
             * clsx doesn't escape the applied classes. viewTransitionClass won't work
             * if the value is not a valid [custom-ident](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Values/custom-ident),
             * and some build tools mangle CSS-module classes in a way that it's not a valid
             * ident and requires escaping.
             */
            CSS.escape(styles['sapphire-toast-view-transition']),
            {
              [CSS.escape(styles[`sapphire-toast-view-transition--reverse`])]:
                placement === 'top',
            }
          ),
          zIndex:
            Number(zIndex) + (queue?.visibleToasts.length ?? 0) - index - 1,
          '--sapphire-toast-index': index,
        } as React.CSSProperties
      }
      ref={ref}
    >
      {wrappToastContentInTheme(
        <div
          className={clsx(styles['sapphire-toast__content'], {
            [styles['sapphire-toast__content--success']]:
              toast.content.variant === 'success',
            [styles['sapphire-toast__content--error']]:
              toast.content.variant === 'error',
          })}
        >
          <ToastContent
            className={clsx(styles['sapphire-toast__text-content'])}
          >
            {/** If title is not provided, use content as title for accessibility */}
            <AriaText slot="title">
              {toast.content.title ?? toast.content.content}
            </AriaText>
            {toast.content.title && toast.content.content && (
              <AriaText slot="description">{toast.content.content}</AriaText>
            )}
          </ToastContent>

          <CloseButton size="sm" aria-label="Close">
            <CloseLarge />
          </CloseButton>
        </div>
      )}
    </AriaToast>
  );
};

const CloseButton = (props: IconButtonProps<'button'>) => {
  [props] = useContextProps({ ...props, slot: 'close' }, null, ButtonContext);

  return (
    <IconButton {...props}>
      <CloseLarge />
    </IconButton>
  );
};
